import type { FieldValueResult } from '@/components/documents/Foxit/FormMapping/composables/FieldValueProvider'
import {
	useFormFillHelper,
	type MappedCartItem,
} from '@/components/documents/Foxit/FormMapping/composables/FormFillHelper'
import { PbProductType } from '@/enums/PbProductType'
import { usePresentationLocale } from '@/i18n'

const categoryToSupplementalSection = {
	[PbProductType.Service]: 'supplementalServices',
	[PbProductType.Package]: null,
	[PbProductType.CashAdvance]: 'supplementalCashAdvance',
	[PbProductType.Merchandise]: 'supplementalMerchandise',
	[PbProductType.Transportation]: 'supplementalServices',
	[PbProductType.Disposition]: null,
	[PbProductType.FamilyTravel]: 'supplementalCashAdvance',
	[PbProductType.Cemetery]: 'supplementalCashAdvance',
	[PbProductType.Afterward]: null,
	[PbProductType.Other]: 'supplementalCashAdvance',
	[PbProductType.Everplans]: null,
	[PbProductType.Oap]: null,
}
type SupplementalPageItems = Map<string, Map<string, MappedCartItem[]>[]>
export const useSupplementalFields = () => {
	const { fieldNameProvider, getFieldPrice, getItemPrice } = useFormFillHelper()

	const getSupplementalFields = (fieldValues: FieldValueResult[]) => {
		const supplementalPageItems = prepareSupplementalPageItems(fieldValues)

		return getSupplementalFieldValues(supplementalPageItems)
	}

	// Prepares a map of supplemental items by section and subcategory
	// This information is used to print out the total of items, description and name of items belonging to the same subcategory
	// It is also sectioned for easier lookup when printed out on the supplemental page
	const prepareSupplementalPageItems = (fieldValues: FieldValueResult[]) => {
		const supplementalPageItems: SupplementalPageItems = new Map()

		fieldValues.forEach((fieldValue) => {
			if (
				fieldValue.supplementalItems?.length &&
				fieldValue.supplementalItems.length > 1
			) {
				const section =
					categoryToSupplementalSection[
						fieldValue.supplementalItems[0]?.category! as PbProductType
					]
				const subcategory = fieldValue.supplementalItems[0]?.subcategory!

				if (!section) {
					return
				}

				const newItem = new Map<string, MappedCartItem[]>().set(
					subcategory,
					fieldValue.supplementalItems
				)

				if (!supplementalPageItems.has(section)) {
					supplementalPageItems.set(section, [newItem])
				} else {
					const existingItems = supplementalPageItems.get(section) as Map<
						string,
						MappedCartItem[]
					>[]
					supplementalPageItems.set(section, [...existingItems, newItem])
				}
			}
		})

		return supplementalPageItems
	}

	// build out the form filling values for the supplemental page
	const getSupplementalFieldValues = (
		supplementalPageItems: Map<string, Map<string, MappedCartItem[]>[]>
	) => {
		const result: {
			name: string
			value: string
			section: string
			format: boolean
		}[] = []

		const { t } = usePresentationLocale()

		supplementalPageItems.forEach((section, fieldName) => {
			let fieldDescription = ''
			section.forEach((subcategory) => {
				subcategory.forEach(
					(items: MappedCartItem[], subcategoryName: string) => {
						const localizedSubcategoryName = t(
							`productSubcategories.${subcategoryName}`
						)
						let itemDescriptions = ''
						items.forEach((item) => {
							itemDescriptions +=
								` \t${item?.name}:` +
								` ${item?.shortDescription}` +
								` ${getFormattedPriceForSupplementalPage(getItemPrice(item))}\n`
						})

						const totalPrice = getFormattedPriceForSupplementalPage(
							getFieldPrice(items) as number
						)
						fieldDescription += `${localizedSubcategoryName}:\n${itemDescriptions} \tSubtotal: ${totalPrice}\n\n`
					}
				)
			})

			result.push({
				name: fieldNameProvider[fieldName],
				value: fieldDescription,
				section: 'supplemental',
				format: false,
			})
		})

		return result
	}

	const getFormattedPriceForSupplementalPage = (
		price: number | null | undefined | 'Incl'
	) => {
		if (
			price === undefined ||
			price === null ||
			Number.isNaN(price) ||
			price === 'Incl'
		) {
			return price
		}

		return `$${price.toFixed(2)}`
	}

	return {
		getSupplementalFields,
		getSupplementalFieldValues,
	}
}
